/**
 * Centralised preorder type conversion mapper.
 *
 * This is the single source of truth for converting between the two preorder
 * representations used in the codebase:
 *
 * - `Preorder` / `Item` (BookingV4): amounts are **numbers in pounds**
 *   (e.g. 50 = £50.00)
 * - `bookingsPreorder` / `bookingsPreorderItem` (ViewBooking): amounts are
 *   **strings in pence** (e.g. "5000" = £50.00)
 *
 * ALL pence↔pounds and string↔number conversions for preorders should happen
 * exclusively through these functions to prevent the x100 type mismatch that
 * causes the "2x 00" price corruption bug (AB#2210242).
 */

import {
  Preorder,
  Item,
  bookingsPreorder,
  bookingsPreorderItem
} from "../types/bookings";

const safeNumber = (value: string | number): number => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Convert a ViewBooking preorder (string pence) to a BookingV4 preorder
 * (number pounds).
 */
export const viewPreorderToV4 = (preorder: bookingsPreorder): Preorder => ({
  id: preorder.id,
  created_date: preorder.created_date,
  name: preorder.name,
  email: preorder.email,
  items: preorder.items?.map(viewPreorderItemToV4) ?? [],
  total_cost: safeNumber(preorder.total_cost) / 100,
  amount_paid: safeNumber(preorder.amount_paid) / 100,
  source: preorder.source,
  status: preorder.status,
  service_charge_percentage: preorder.service_charge_percentage,
  service_charge_amount: safeNumber(preorder.service_charge_amount) / 100
});

/**
 * Convert a ViewBooking preorder item (string pence) to a BookingV4 item
 * (number pounds).
 */
export const viewPreorderItemToV4 = (item: bookingsPreorderItem): Item => ({
  id: item.id,
  name: item.name,
  quantity: item.quantity,
  total: safeNumber(item.total) / 100,
  base_price: safeNumber(item.base_price) / 100,
  options_price: safeNumber(item.options_price) / 100,
  discount_value: safeNumber(item.discount_value) / 100,
  description: item.description,
  plu: item.plu,
  sub_type: item.sub_type,
  surcharge: item.surcharge,
  type: item.type,
  price: safeNumber(item.base_price) / 100,
  for_name: "",
  items: []
});

/**
 * Convert a BookingV4 preorder (number pounds) to a ViewBooking preorder
 * (string pence).
 */
export const v4PreorderToView = (preorder: Preorder): bookingsPreorder => ({
  id: preorder.id,
  created_date: preorder.created_date,
  name: preorder.name,
  email: preorder.email,
  booking_id: "",
  venue_id: "",
  venue_group: "",
  items: preorder.items?.map(v4PreorderItemToView) ?? [],
  total_cost: String(Math.round(preorder.total_cost * 100)),
  amount_paid: String(Math.round(preorder.amount_paid * 100)),
  source: preorder.source,
  status: preorder.status,
  service_charge_percentage: preorder.service_charge_percentage,
  service_charge_amount: String(
    Math.round(preorder.service_charge_amount * 100)
  )
});

/**
 * Convert a BookingV4 item (number pounds) to a ViewBooking preorder item
 * (string pence).
 */
export const v4PreorderItemToView = (item: Item): bookingsPreorderItem => ({
  id: item.id,
  name: item.name,
  quantity: item.quantity,
  total: String(Math.round(item.total * 100)),
  base_price: String(Math.round(item.base_price * 100)),
  options_price: String(Math.round(item.options_price * 100)),
  discount_value: String(Math.round(item.discount_value * 100)),
  description: item.description ?? "",
  plu: item.plu,
  sub_type: item.sub_type,
  surcharge: null,
  type: item.type
});

/**
 * Runtime validation that logs a console.error if preorder field types are
 * wrong (e.g. `total_cost` is a string instead of a number). This acts as a
 * safety net to catch accidental type mismatches before they reach the API.
 */
export const assertPreorderTypes = (
  preorder: Preorder,
  context: string
): void => {
  if (
    typeof preorder.total_cost !== "number" ||
    Number.isNaN(preorder.total_cost)
  ) {
    console.error(
      `[${context}] preorder.total_cost should be a number but got ${typeof preorder.total_cost}:`,
      preorder.total_cost
    );
  }

  if (
    typeof preorder.amount_paid !== "number" ||
    Number.isNaN(preorder.amount_paid)
  ) {
    console.error(
      `[${context}] preorder.amount_paid should be a number but got ${typeof preorder.amount_paid}:`,
      preorder.amount_paid
    );
  }

  if (
    typeof preorder.service_charge_amount !== "number" ||
    Number.isNaN(preorder.service_charge_amount)
  ) {
    console.error(
      `[${context}] preorder.service_charge_amount should be a number but got ${typeof preorder.service_charge_amount}:`,
      preorder.service_charge_amount
    );
  }

  if (preorder.items) {
    preorder.items.forEach((item, index) => {
      if (typeof item.total !== "number" || Number.isNaN(item.total)) {
        console.error(
          `[${context}] preorder.items[${index}].total should be a number but got ${typeof item.total}:`,
          item.total
        );
      }
      if (
        typeof item.base_price !== "number" ||
        Number.isNaN(item.base_price)
      ) {
        console.error(
          `[${context}] preorder.items[${index}].base_price should be a number but got ${typeof item.base_price}:`,
          item.base_price
        );
      }
      if (
        typeof item.options_price !== "number" ||
        Number.isNaN(item.options_price)
      ) {
        console.error(
          `[${context}] preorder.items[${index}].options_price should be a number but got ${typeof item.options_price}:`,
          item.options_price
        );
      }
      if (
        typeof item.discount_value !== "number" ||
        Number.isNaN(item.discount_value)
      ) {
        console.error(
          `[${context}] preorder.items[${index}].discount_value should be a number but got ${typeof item.discount_value}:`,
          item.discount_value
        );
      }
    });
  }
};
